import ContentsToReact from '@/components/ContentsToReact/ContentsToReact';
import GenericModals from '@/components/GenericModals/GenericModals';
import ItemToComponent from '@/components/ItemToComponent/ItemToComponent';
import Page from '@/components/Page/Page';
import { extractContents } from '@/helpers/extractContents';
import extractPageMetaFromRouteData from '@/helpers/extractPageMetaFromRouteData';
import { useInitialPageDataLayer } from '@/hooks/useInitialPageDataLayer/useInitialPageDataLayer';
import { useQueryParams } from '@/hooks/useQueryParams/useQueryParams';
import { PageModel } from '@/models/page';
import EnercityFooter from '@uikit/components/EnercityFooter/EnercityFooter';
import HeroGallery from '@uikit/components/HeroGallery/HeroGallery';
import Layout from '@uikit/components/Layout/Layout';
import LegacyHeader from '@uikit/components/LegacyHeader/LegacyHeader.wrapper';
import Section from '@uikit/components/Section/Section';
import Spacer from '@uikit/components/Spacer/Spacer';
import type { Asset } from '@uikit/types';
import Head from 'next/head';
import { CmsComponentsContext } from '../CmsComponentsContext';
import type { WaermewendeProjectPageProps } from './types';
import { cmsComponents } from './WaermewendeProjectPage.cmsComponents';
import WaermwendeProjectContainer from './WaermwendeProjectContainer/WaermwendeProjectContainer';
import { WaermwendeProjectInfo } from './WaermwendeProjectInfo/WaermwendeProjectInfo';

const WaermewendeProjectPage = (
  routeData: PageModel<WaermewendeProjectPageProps>
) => {
  const {
    contents,
    modals,
    path,
    props: {
      title,
      subTitle,
      category,
      image,
      constructionStart,
      operationStart,
      capacity,
      city,
      postalCode,
      street,
      houseNumber,
    },
  } = routeData;
  const meta = extractPageMetaFromRouteData(routeData, {
    ogType: 'article',
  });

  const {
    extractedContents: [headerArea, additionalContentsArea, galleryContents],
    restContents,
  } = extractContents(['header', 'additional-contents', 'gallery'], contents);

  const [{ contents: additionalContents }] = additionalContentsArea ?? [{}];

  const parentPagePath = path.substring(0, path.lastIndexOf('/')) || '/';

  useInitialPageDataLayer([
    {
      event: 'pageView',
    },
  ]);

  const galleryImages: Asset[] = [
    image,
    ...(galleryContents?.[0]?.contents?.map((item) => item.props?.image) ?? []),
  ];

  const [header] = headerArea;

  const { params } = useQueryParams();

  const isPageEmbed =
    ('embed' in params && !params.embed) || params.embed === 'true';

  return (
    <CmsComponentsContext.Provider value={cmsComponents}>
      <Head>
        <meta httpEquiv="X-Frame-Options" content="" />
      </Head>

      <Layout variant="fullWidth" noFooterSpace={isPageEmbed}>
        <Page meta={meta}>
          {isPageEmbed ? (
            <Spacer defaultSize={1} />
          ) : (
            <LegacyHeader
              {...header.props}
              isFullWidth
              invert={false}
              noSticky={false}
            />
          )}

          <Section resetTopSpace>
            <HeroGallery images={galleryImages} />

            <WaermwendeProjectContainer>
              <WaermwendeProjectInfo
                backButtonPath={isPageEmbed ? null : parentPagePath}
                title={title}
                subTitle={subTitle}
                category={category}
                constructionStart={constructionStart}
                operationStart={operationStart}
                capacity={capacity}
                city={city}
                postalCode={postalCode}
                street={street}
                houseNumber={houseNumber}
              />
            </WaermwendeProjectContainer>

            {restContents?.map((item) => {
              const wrapperVariant = item.componentName?.includes('image')
                ? 'wide'
                : 'default';

              return (
                <WaermwendeProjectContainer
                  key={item.id}
                  variant={wrapperVariant}
                >
                  <ItemToComponent item={item} />
                </WaermwendeProjectContainer>
              );
            })}
          </Section>

          {additionalContents && (
            <ContentsToReact contents={additionalContents} />
          )}
        </Page>

        {!isPageEmbed && <EnercityFooter />}
      </Layout>
      <GenericModals modals={modals} />
    </CmsComponentsContext.Provider>
  );
};

export default WaermewendeProjectPage;
